/**
 * CookieConsentBanner Component
 * GDPR-compliant cookie consent banner for analytics opt-in
 * Features:
 * - Keyboard accessible (ESC to deny, Tab navigation)
 * - ARIA dialog with focus trap
 * - localStorage persistence
 * - Link to cookie policy
 */

import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Button from '../ui/Button';

const CONSENT_KEY = 'consent.analytics';

export default function CookieConsentBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const bannerRef = useRef(null);
  const acceptButtonRef = useRef(null);

  useEffect(() => {
    // Check if consent decision has already been made
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) {
      setIsVisible(true);
    }
  }, []);

  useEffect(() => {
    if (isVisible && acceptButtonRef.current) {
      // Focus the accept button when banner becomes visible
      acceptButtonRef.current.focus();
    }
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    // Handle ESC key to deny
    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        handleDeny();
      }
    }

    // Simple focus trap: keep focus within banner
    function handleFocusTrap(e) {
      if (!bannerRef.current) return;

      const focusableElements = bannerRef.current.querySelectorAll(
        'button, a[href], [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.key === 'Tab') {
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keydown', handleFocusTrap);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keydown', handleFocusTrap);
    };
  }, [isVisible]);

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, 'granted');
    setIsVisible(false);

    // Initialize analytics
    const analytics = require('../../services/analytics');
    analytics.initializeAnalytics();
  };

  const handleDeny = () => {
    localStorage.setItem(CONSENT_KEY, 'denied');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div
      ref={bannerRef}
      role="dialog"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-description"
      aria-modal="true"
      className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t-2 border-gray-200 shadow-lg p-4 md:p-6"
      style={{ maxWidth: '100vw' }}
    >
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1">
            <h2 id="cookie-consent-title" className="text-lg font-semibold text-gray-900 mb-2">
              {"Cookies d'analyse"}
            </h2>
            <p id="cookie-consent-description" className="text-sm text-gray-700">
              {"Nous utilisons des cookies d'analyse anonymisés pour améliorer AnimFacile. Pas de"}
              publicité. Vous pouvez refuser.{' '}
              <Link
                to="/legal/cookies"
                className="text-indigo-600 underline hover:text-indigo-800 focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 rounded"
              >
                En savoir plus
              </Link>
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-2">
            <Button
              variant="secondary"
              onClick={handleDeny}
              className="min-w-[100px] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Refuser
            </Button>
            <Button
              ref={acceptButtonRef}
              variant="primary"
              onClick={handleAccept}
              className="min-w-[100px] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-600"
            >
              Accepter
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
